import { Link } from 'react-router';
import { Cuboid, Eye, Flame, Shapes, Sparkles, Tag } from 'lucide-react';
import { ThumbImage } from '../ThumbImage';
import { Mark } from './Mark';
import { isVariantable, variantUrl } from '#shared/image-url';
import type { Card } from '#shared/types/public';

/** De regel onder de titel — het enige op de kaart waar een bezoeker aan kan
 *  zien of hij moet klikken.
 *
 *  Hier stond alleen `subject`, en dat is in 83 van de 125 gepubliceerde
 *  artikelen letterlijk de titel. De kaart verbergt hem dan (anders staat de
 *  kop er twee keer), dus twee derde van de kaarten had onder de titel niets
 *  staan: plaatje, kop, chips, klaar.
 *
 *  Is subject wél geschreven als samenvatting — 60 tekens of meer, "Deze
 *  handleiding beschrijft hoe…" — dan wint die van de tekst uit het artikel,
 *  want een redacteur heeft hem met opzet zo neergezet. In alle andere gevallen
 *  de eerste alinea van het artikel zelf (`excerpt`, zie makeExcerpt in
 *  src/search.js), precies zoals ArticleTile in het beheer dat al doet.
 *
 *  Op de ZOEKpagina wint het fragment rond de treffer (`snippet`) van alles:
 *  wie op "wachtbon" zoekt wil de zin zien waar dat woord in staat, niet de
 *  inleiding van het artikel. */
function blurbOf(item: Card): string | null {
  if (item.snippet) return item.snippet;
  const subject = item.subject && item.subject.trim() !== item.name.trim() ? item.subject.trim() : null;
  if (subject && subject.length >= 60) return subject;
  return item.excerpt || subject;
}

/* De miniatuur is 16:9 in een kolom van hoogstens ~400 px; de 480-variant
   dekt dat scherp, en 800 is er voor schermen met dubbele pixeldichtheid.
   Zonder dit haalde een kaart het ORIGINEEL op — tot 21 MB voor één tegel. */
function thumbSources(src: string): { src: string; srcSet?: string } {
  if (!isVariantable(src)) return { src };
  return {
    src: variantUrl(src, 480),
    srcSet: `${variantUrl(src, 480)} 480w, ${variantUrl(src, 800)} 800w`,
  };
}

/** De kaart van de publieke grid. Vervangt zes gedupliceerde markup-blokken
 *  (home latest, home popular, zoekresultaten, categorie, gerelateerd, leerpad)
 *  door één component.
 *
 *  `q` is de zoekopdracht waar deze kaart uit voortkomt: die gaat als ?q= mee
 *  naar het artikel, dat de woorden dan in de tekst markeert. */
export function ArticleCard({ item, badge, q }: { item: Card; badge?: 'nieuw' | 'populair'; q?: string }) {
  const href = `/artikel/${encodeURIComponent(item.slug || item.id)}${q ? `?q=${encodeURIComponent(q)}` : ''}`;
  const blurb = blurbOf(item);
  /* VERVANGEN, niet uitgebreid: primair thema + hoogstens één categorie + één
     module. Voorheen waren dit er 2+2. De kaart wordt dus niet drukker (3 in
     plaats van 4 chips) maar wél vól waar hij nu leeg is: thema's dekken 99%
     van het corpus, categorieën 34%. Het thema staat vooraan omdat het het
     enige label is dat een bezoeker bijna altijd ziet. */
  const tags = [
    ...item.themes.slice(0, 1).map((t) => ({ label: t, kind: 'theme' as const })),
    ...item.categories.slice(0, 1).map((c) => ({ label: c, kind: 'cat' as const })),
    ...item.modules.slice(0, 1).map((m) => ({ label: m, kind: 'mod' as const })),
  ];

  /* Eén placeholder voor twee gevallen: geen afbeelding in het artikel, en
     een afbeelding die niet laadt. Zie ThumbImage voor het tweede geval.
     Het logo staat op logo-optical omdat het beeldmerk links licht is en het
     woordmerk rechts zwaar — exact centreren oogt scheef. Zie theme.css. */
  const thumbFallback = (
    <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-paper-200 to-paper-300 dark:from-slate-800 dark:to-slate-900">
      <img src="/leza-logo.png" alt="" className="logo-optical h-10 w-auto opacity-40" />
    </div>
  );

  return (
    <article className="group relative flex flex-col overflow-hidden rounded-lg border border-hairline bg-surface shadow-sm transition-[box-shadow,border-color] duration-300 hover:border-accent hover:shadow-glow">
      {/* prefetch="intent": zodra de muis erboven hangt of de kaart focus
          krijgt, haalt de browser de route en de loaderdata al op. De klik
          daarna is dan vrijwel direct — het verschil tussen "even wachten" en
          "staat er al". */}
      <Link viewTransition prefetch="intent" to={href} className="absolute inset-0 z-1" aria-label={`Open ${item.name}`} />

      <div className="relative aspect-[16/9] overflow-hidden bg-sunken">
        {item.firstImageUrl ? (
          <ThumbImage
            {...thumbSources(item.firstImageUrl)}
            sizes="(min-width: 1024px) 400px, (min-width: 640px) 50vw, 100vw"
            fallback={thumbFallback}
            className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-105"
          />
        ) : (
          thumbFallback
        )}

        {badge === 'nieuw' && (
          <span className="absolute top-2.5 left-2.5 z-2 rounded-full bg-accent px-2.5 py-0.5 text-2xs font-semibold tracking-wide text-accent-on uppercase shadow-sm">
            <Sparkles size={9} className="mr-1 inline align-[-1px]" aria-hidden />
            Nieuw
          </span>
        )}
        {badge === 'populair' && (
          <span className="absolute top-2.5 right-2.5 z-2 inline-flex items-center gap-1 rounded-full bg-ink-900/72 px-2.5 py-0.5 text-2xs font-semibold tracking-wide text-paper-0 uppercase backdrop-blur-sm">
            <Flame size={9} className="text-accent" aria-hidden />
            Populair
          </span>
        )}
        {typeof item.percent === 'number' && (
          <span className="absolute right-2.5 bottom-2.5 z-2 rounded-full bg-accent px-2 py-0.5 text-2xs font-bold text-accent-on shadow-sm">
            {item.percent}%
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-1.5 p-4">
        <h3 className="line-clamp-2 text-lg leading-snug font-semibold text-ink transition-colors group-hover:text-ink">
          <Mark text={item.name} hits={item.hits} />
        </h3>
        {blurb && (
          <p className="line-clamp-3 text-base text-ink-3">
            <Mark text={blurb} hits={item.hits} />
          </p>
        )}

        {tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {tags.map((t) => (
              <span
                key={`${t.kind}-${t.label}`}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
                  t.kind === 'theme' ? 'bg-accent-soft text-accent-soft-ink' : 'bg-sunken text-ink-2'
                }`}
              >
                {t.kind === 'theme' ? <Shapes size={9} aria-hidden />
                  : t.kind === 'cat' ? <Tag size={9} aria-hidden /> : <Cuboid size={9} aria-hidden />}
                {t.label}
              </span>
            ))}
          </div>
        )}

        <div className="mt-auto flex items-center justify-between pt-2 text-sm text-ink-3">
          <span>{item.updatedAtStr}</span>
          <span className="flex items-center gap-1">
            <Eye size={12} aria-hidden />
            {item.views}
          </span>
        </div>
      </div>
    </article>
  );
}

export function ArticleCardGrid({ items, badge, q }: { items: Card[]; badge?: 'nieuw' | 'populair'; q?: string }) {
  return (
    /* MAXIMAAL DRIE PER RIJ. Hier stond xl:grid-cols-4, en op een breed scherm
       werden dat vier smalle kaartjes: de titel brak in tweeën, de samenvatting
       viel weg achter de klem en de chiprij wikkelde. Bij drie kolommen is de
       kaart breed genoeg dat de tekst erin ook echt leesbaar is. */
    <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((item) => (
        <ArticleCard key={item.id} item={item} badge={badge} q={q} />
      ))}
    </div>
  );
}
